import { validateAgeStartedGaming, calculateCurrentAge, validatePhone } from '@/shared/utils'
import type { EnrollmentData } from '@/features/enrollment'

// Validation function for Step2
export const validateStep2 = (data: EnrollmentData, legalGuardianConfirmed: boolean = false) => {
  const errors: { [key: string]: string } = {}

  if (!data.relationToVictim) errors.familyMemberAddicted = 'This field is required. Please select a value.'
  if (!data.birthMonth) errors.birthMonth = 'Month is required'
  if (!data.birthDay) errors.birthDay = 'Day is required'
  if (!data.birthYear) errors.birthYear = 'Year is required'

  // Age validation - check against campaign settings
  if (data.birthYear && data.birthMonth && data.birthDay) {
    const currentAge = calculateCurrentAge(data.birthYear, data.birthMonth, data.birthDay)

    // ✅ Use campaign minAge/maxAge settings if available
    const campaign = data.campaign
    const minAge = campaign?.minAge ?? null
    const maxAge = campaign?.maxAge ?? null

    // Check if age is too old (exceeds maxAge)
    if (maxAge !== null && currentAge && currentAge > maxAge) {
      errors.gamerAge = `Gamer is ${currentAge} years old and not eligible for this campaign. Only gamers aged ${maxAge} and younger are eligible.`
    }

    // Check if age is too young (below minAge)
    if (minAge !== null && currentAge && currentAge < minAge) {
      errors.gamerAge = `Gamer is ${currentAge} years old and not eligible for this campaign. Only gamers aged ${minAge} and older are eligible.`
    }
    
    // New age-based validation logic
    if (data.relationToVictim === 'Myself' && currentAge && currentAge < 18) {
      // "Myself" selected but gamer is under 18
      errors.minorError = 'A parent or legal guardian must be present and must sign up the gamer because the gamer is a minor under the age of 18.'
    }
    
    if (data.relationToVictim === 'Loved one' && currentAge && currentAge >= 18 && !legalGuardianConfirmed) {
      // "Loved One" selected, gamer is 18+, but legal guardian not confirmed
      errors.legalGuardianRequired = 'You must confirm that you are the legal guardian of this adult to proceed.'
    }
  }
  
  // Enhanced validation for age when started gaming using utility function
  if (!data.ageStartedGaming) {
    errors.ageStartedGaming = 'Age when started gaming is required'
  } else {
    const validationResult = validateAgeStartedGaming(
      data.ageStartedGaming,
      data.birthYear,
      data.birthMonth,
      data.birthDay
    )
    
    if (!validationResult.isValid) {
      errors.ageStartedGaming = validationResult.errorMessage || 'Invalid age'
    }
  }
  if (!data.hoursPerDay) {
    errors.hoursPerDay = 'Hours per day is required'
  } else {
    const hours = parseFloat(data.hoursPerDay)
    if (isNaN(hours) || hours < 0.1) {
      errors.hoursPerDay = 'Please enter a valid number of hours'
    } else if (hours < 3) {
      errors.hoursPerDay = 'Must be at least 3 hours per day'
    } else if (hours > 24) {
      errors.hoursPerDay = 'Cannot exceed 24 hours per day'
    }
  }
  if (!data.intakeFirstName) errors.intakeFirstName = 'First Name is required'
  if (!data.intakeLastName) errors.intakeLastName = 'Last Name is required'
  
  // Validate caller fields when "Loved One" is selected
  if (data.relationToVictim === 'Loved one') {
    if (!data.callerFirstName) errors.callerFirstName = 'Caller First Name is required'
    if (!data.callerLastName) errors.callerLastName = 'Caller Last Name is required'
  }
  
  // Phone validation with American format
  if (!data.intakePhone) {
    errors.intakePhone = 'Phone is required'
  } else {
    const phoneValidation = validatePhone(data.intakePhone)
    if (!phoneValidation.isValid) {
      errors.intakePhone = phoneValidation.error || 'Phone number must be a valid American phone number'
    }
  }
  if (!data.intakeEmail) errors.intakeEmail = 'Email is required'
  if (!data.intakeZipCode) errors.intakeZipCode = 'ZIP Code is required'
  if (!data.intakeConsent) errors.intakeConsent = 'Consent is required'
  
  return errors
}
